import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, Target, Eye } from 'lucide-react';
import { Counter } from '@/hooks/useCountUp';
import { TextReveal } from '@/hooks/useAnimations';

const ease = [0.22, 1, 0.36, 1];

const CorporatePage = () => {
  const values = [
    { title: 'Güvenlik', desc: 'Her operasyonda birinci öncelik.' },
    { title: 'Dakiklik', desc: 'Zamanında teslimat, güvenin temeli.' },
    { title: 'Kontrol', desc: 'Süreçlerin her adımında denetim.' },
  ];

  const milestones = [
    { year: '1998', event: 'Kuruluş. Kararlı adımlarla sektöre giriş.' },
    { year: '2005', event: 'ADR sertifikasyonu, tehlikeli madde operasyonlarına başlangıç.' },
    { year: '2010', event: 'Operasyonel kapasitenin genişletilmesi.' },
    { year: '2015', event: 'GPS takip sistemleri entegrasyonu, dijital dönüşüm.' },
    { year: '2020', event: 'Türkiye genelinde yaygın kapsama.' },
    { year: '2024', event: 'Dijital operasyon altyapısının tamamlanması.' },
  ];

  return (
    <div data-testid="corporate-page" className="bg-white">
      <section className="page-hero-banner">
        <img src="/images/hero-slide-4.webp" alt="" className="hero-bg" loading="eager" />
        <div className="hero-gradient" />
        <div className="hero-inner">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="max-w-2xl">
            <span className="inline-block text-[#FF6B2C] text-xs font-semibold tracking-[0.2em] uppercase mb-4">Kurumsal</span>
            <h1 className="heading-xl text-white mb-4">Disiplinli yapı. Güvenilir süreç.</h1>
            <p className="text-base text-white/70 leading-relaxed max-w-lg">1998'den bu yana aynı kararlılıkla.</p>
          </motion.div>
        </div>
      </section>

      <section className="section-white section-padding">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
            <div>
              <TextReveal><div className="section-label"><span>Hikayemiz</span></div></TextReveal>
              <TextReveal delay={0.1}><h2 className="heading-lg text-[#0B3A82] mb-6">Kararlı bir başlangıç. Tutarlı bir yolculuk.</h2></TextReveal>
              <TextReveal delay={0.15}>
                <div className="space-y-5 text-body-lg">
                  <p>Asküçükler Lojistik, 1998 yılında tek bir ilkeyle kuruldu: <strong className="text-[#1A1A2E]">"Güvenlik ve güvenilirlik, her şeyden önce gelir."</strong></p>
                  <p>Bu ilke değişmedi. Deneyimli kadromuz, ADR sertifikalı operasyonlarımız ve 7/24 kesintisiz hizmet anlayışımızla aynı disiplinle çalışmaya devam ediyoruz.</p>
                </div>
              </TextReveal>
            </div>
            <div className="grid grid-cols-2 gap-5">
              {[{ v: 27, s: '+', l: 'Yıllık Deneyim' }, { v: 100, s: '%', l: 'ADR Uyumlu' }, { v: 7, s: '/24', l: 'Kesintisiz Hizmet' }].map((stat, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08, ease }}
                  className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-2xl p-6 text-center"
                >
                  <div className="text-2xl font-extrabold text-[#0B3A82] mb-1 font-['Manrope']"><Counter end={stat.v} duration={2000} />{stat.s}</div>
                  <div className="text-xs text-[#64748B] font-medium">{stat.l}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="section-gray section-padding">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid md:grid-cols-2 gap-8">
          {[{ icon: Target, title: 'Misyonumuz', text: 'En yüksek güvenlik standartlarını koruyarak, kontrollü, güvenilir ve verimli lojistik operasyonlar yürütmek.' },
            { icon: Eye, title: 'Vizyonumuz', text: 'Türkiye\'nin en güvenilir akaryakıt taşımacılığı firması olmak. Teknolojiyi operasyona entegre ederek sektörde standart belirlemek.' }].map((item, i) => (
            <motion.div key={item.title} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1, ease }}
              className="bg-white border border-[#E2E8F0] rounded-2xl p-10"
            >
              <div className="icon-box mb-6"><item.icon className="w-5 h-5" /></div>
              <h3 className="text-lg font-bold text-[#0B3A82] mb-4">{item.title}</h3>
              <p className="text-body leading-relaxed">{item.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="section-white section-padding">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-24">
            <div>
              <TextReveal><div className="section-label"><span>Değerlerimiz</span></div></TextReveal>
              <TextReveal delay={0.1}><h2 className="heading-lg text-[#0B3A82] mb-6">Bizi biz yapan ilkeler.</h2></TextReveal>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              {values.map((v, i) => (
                <motion.div key={v.title} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06, ease }}
                  className="p-6 card-refined rounded-2xl"
                >
                  <div className="w-8 h-8 rounded-lg bg-[#0B3A82]/6 flex items-center justify-center mb-4">
                    <span className="text-sm font-extrabold text-[#0B3A82]">0{i + 1}</span>
                  </div>
                  <h3 className="text-sm font-bold text-[#1A1A2E] mb-2">{v.title}</h3>
                  <p className="text-sm text-[#64748B] leading-relaxed">{v.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="section-gray section-padding">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <TextReveal><div className="section-label"><span>Kilometre Taşlarımız</span></div></TextReveal>
          <TextReveal delay={0.1}><h2 className="heading-lg text-[#0B3A82] mb-12">Tutarlı adımlar.</h2></TextReveal>
          <div className="max-w-2xl space-y-0">
            {milestones.map((m, i) => (
              <motion.div key={m.year} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06, ease }}
                className="flex gap-6 relative pb-8 last:pb-0"
              >
                <div className="flex flex-col items-center">
                  <div className="w-2.5 h-2.5 bg-[#FF6B2C] rounded-full flex-shrink-0 z-10" />
                  {i < milestones.length - 1 && <div className="w-px flex-1 bg-[#E2E8F0] mt-2" />}
                </div>
                <div>
                  <div className="text-base font-extrabold text-[#0B3A82] mb-1">{m.year}</div>
                  <p className="text-sm text-[#64748B]">{m.event}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="cta-section section-padding-sm">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center relative z-10">
          <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="heading-lg text-white mb-6">Operasyonunuzu birlikte planlayalım.</h2>
            <Link to="/iletisim">
              <Button className="bg-[#FF6B2C] hover:bg-[#E55A1F] text-white rounded-full font-semibold px-10 py-6 text-base shadow-lg group" data-testid="corporate-cta-button">
                Ekibimizle görüşün <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default CorporatePage;
